import { supabase } from '@/infrastructure/supabase/client'
import { Tenant } from '@/domain/model/Tenant'
import type { ITenantRepository, CreateTenantInput } from '@/application/port/out/ITenantRepository'
import type { Plan, CustomPlanLimits, Tables, TablesUpdate } from '@brix/types'

function toEntity(row: Tables<'tenants'>): Tenant {
  return Tenant.reconstitute({
    id: row.id,
    name: row.name,
    slug: row.slug,
    plan: row.plan as Plan,
    customLimits: (row.custom_limits ?? null) as unknown as CustomPlanLimits | null,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  })
}

export class SupabaseTenantRepository implements ITenantRepository {
  async findById(id: string): Promise<Tenant | null> {
    const { data } = await supabase.from('tenants').select('*').eq('id', id).single()
    return data ? toEntity(data) : null
  }

  async findBySlug(slug: string): Promise<Tenant | null> {
    const { data } = await supabase.from('tenants').select('*').eq('slug', slug).single()
    return data ? toEntity(data) : null
  }

  async findByUserId(userId: string): Promise<Tenant | null> {
    const { data } = await supabase
      .from('users')
      .select('tenants(*)')
      .eq('id', userId)
      .single()
    if (!data) return null
    return data.tenants ? toEntity(data.tenants) : null
  }

  async create(input: CreateTenantInput): Promise<Tenant> {
    const { data, error } = await supabase
      .rpc('create_tenant', {
        p_name: input.name,
        p_slug: input.slug,
        p_owner_id: input.ownerUserId,
      })
    if (error ?? !data?.[0]) throw new Error(error?.message ?? 'Failed to create tenant')
    const row = data[0]
    return Tenant.reconstitute({
      id: row.id,
      name: row.name,
      slug: row.slug,
      plan: row.plan as Plan,
      customLimits: null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    })
  }

  async update(id: string, patch: Partial<Tenant>): Promise<Tenant> {
    const db: TablesUpdate<'tenants'> = {}
    if (patch.name !== undefined) db.name = patch.name
    if (patch.plan !== undefined) db.plan = patch.plan

    const { data, error } = await supabase.from('tenants').update(db).eq('id', id).select().single()
    if (error ?? !data) throw new Error(error?.message ?? 'Failed to update tenant')
    return toEntity(data)
  }

  async isSlugAvailable(slug: string): Promise<boolean> {
    const { data } = await supabase.from('tenants').select('id').eq('slug', slug).single()
    return !data
  }
}
